// src/hooks/useWebSocket.js
import { useEffect, useRef, useCallback } from 'react';

export function useWebSocket(onMessage) {
  const ws = useRef(null);
  const reconnectTimer = useRef(null);
  const mounted = useRef(true);

  const connect = useCallback(() => {
    const token = localStorage.getItem('crm_token');
    if (!token || !mounted.current) return;

    const explicitWsUrl = process.env.REACT_APP_WS_URL;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname;
    const port = process.env.NODE_ENV === 'development' ? '5000' : window.location.port;
    const portSegment = port ? `:${port}` : '';
    const wsUrl = explicitWsUrl
      ? new URL(explicitWsUrl, window.location.href).toString().replace(/^http/, 'ws')
      : `${protocol}//${host}${portSegment}/ws`;

    const socket = new WebSocket(wsUrl);
    ws.current = socket;

    socket.onopen = () => {
      if (ws.current !== socket || socket.readyState !== WebSocket.OPEN) return;
      socket.send(JSON.stringify({ type: 'AUTH', token }));
    };

    socket.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        onMessage && onMessage(msg);
      } catch {}
    };

    socket.onclose = () => {
      if (ws.current === socket) {
        ws.current = null;
      }
      if (mounted.current && ws.current == null) {
        reconnectTimer.current = setTimeout(connect, 5000);
      }
    };

    socket.onerror = () => {
      if (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN) {
        socket.close();
      }
    };
  }, [onMessage]);

  useEffect(() => {
    mounted.current = true;
    connect();
    return () => {
      mounted.current = false;
      clearTimeout(reconnectTimer.current);
      ws.current?.close();
    };
  }, [connect]);

  const send = useCallback((data) => {
    const socket = ws.current;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(data));
    }
  }, []);

  return { send };
}
